import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Animated,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {useIsFocused, useNavigation} from '@react-navigation/native';

import {Button} from '../../../../components/Interface/Forms/Buttons/Button';
import {Input} from '../../../../components/Interface/Forms/Inputs/Input';
import {Loading} from '../../../../components/Interface/Loadings/Loading';

import {LinkButton} from '../../../../components/Interface/Forms/Buttons/LinkButton';

import {
  Container,
  Content,
  Form,
  InputGroup,
  TrustBadgesRow,
  TrustBadgeItem,
  SenatranBadge,
  SenatranSelo,
  SenatranTextWrapper,
  SenatranLabel,
  SenatranTitle,
  TrustBadgeText,
  TrustBadgeDivider,
  SupportContainer,
  SupportText,
  SupportPhone,
} from './styles';

const seloImage = require('../../../../assets/images/selo.webp');
const seloDescontoImage = require('../../../../assets/images/icons/bandeira-do-brasil.webp');

import analytics from '@react-native-firebase/analytics';
import DeviceInfo from 'react-native-device-info';
import {api} from '../../../../services/api';
import {rastrear} from '../../../../services/rastrear';
import {useAuth} from '../../../../context/auth';
import {
  PPConsultaDTO,
  PPDetranDTO,
  PPVeiculoDTO,
} from '../../../../dtos/ProntoPagueiDTO';
import {version as appVersion} from '../../../../../package.json';
import {RFPercentage, RFValue} from 'react-native-responsive-fontsize';

import {useGlobal} from '../../../../context/global';
import {Maintenance} from '../../../../components/Interface/Maintenance';
import {ProfileHeader} from '../../../../components/Interface/Headers/ProfileHeader';
import {ScreenTitle} from '../../../../components/shared/ScreenTitle';

import {VehicleCard} from './components/VehicleCard';
import remoteConfig from '@react-native-firebase/remote-config';
import {
  setShouldShowOTPModal,
  useShouldShowOTPModal,
} from '../../../../context/otpModal';
import {PhoneVerificationModal} from './components/PhoneVerificationModal';
import {CheckoutGetnetLoading} from '../../../../components/shared/Payments/CheckoutGetnetLoading';
import {Picker} from '../../../../components/Interface/Forms/Inputs/Picker';

const VALID_PLATE_LENGTH = 7;
const VALID_CPF_LENGTH = 11;
const VALID_CNPJ_LENGTH = 14;
const VALID_RENAVAM_MIN_LENGTH = 9;
const VEHICLE_FETCH_DEBOUNCE_MS = 600;
const POLL_INTERVAL_MS = 4000;

export function PP_SearchDebits() {
  const navigation = useNavigation();
  const {isLogged, isAuthReady, user, openSign, setShowSign, setSignDismissable} =
    useAuth();
  const isFocused = useIsFocused();

  const {fetchTextoIndParcDebCombustivel, disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);

  // Tela exige autenticação: enquanto não estiver logado, mantém o modal
  // de login aberto e não-dispensável, sem redirecionar (o redirect por
  // navigation.navigate('Home') não existe nas rotas web e não bloqueava
  // o acesso à tela).
  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (!isLogged) {
      setSignDismissable(false);
      openSign();
    } else {
      setShowSign(false);
    }
  }, [isLogged, isAuthReady, openSign, setShowSign, setSignDismissable]);

  const [isLoading, setIsLoading] = useState(false);
  const [uf, setUf] = useState('');
  const [placa, setPlaca] = useState('');
  const [renavam, setRenavam] = useState('');
  const [docProprietario, setDocProprietario] = useState('');

  const [showPhoneVerificationModal, setShowPhoneVerificationModal] =
    useState(false);

  const shouldShowOTPModal = useShouldShowOTPModal();
  const wasOTPModalVisibleRef = useRef(shouldShowOTPModal);
  const otpModalOpenedForScreenTriggerRef = useRef(false);

  const [novoFluxoDebitoVeicular, setNovoFluxoDebitoVeicular] = useState(true);

  const [detrans, setDetrans] = useState<PPDetranDTO[]>([]);
  const [veiculo, setVeiculo] = useState<PPVeiculoDTO | null>(null);
  const [veiculoLoading, setVeiculoLoading] = useState(false);
  const [veiculoError, setVeiculoError] = useState<string | null>(null);
  const [veiculoNaoEncontrado, setVeiculoNaoEncontrado] = useState(false);

  const [veiculoPolling, setVeiculoPolling] = useState(false);

  const [numeroSuporte1, setNumeroSuporte1] = useState('');
  const [numeroSuporte2, setNumeroSuporte2] = useState('');

  const [remoteConfigLoaded, setRemoteConfigLoaded] = useState(false);
  const [phoneSmsModalEnabled, setPhoneSmsModalEnabled] = useState(false);
  const [phoneSmsModalTrigger, setPhoneSmsModalTrigger] = useState<
    'screen' | 'consult'
  >('consult');

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    let isMounted = true;

    async function fetchRemoteConfig() {
      try {
        const config = remoteConfig();

        await config.setDefaults({
          Numero_Suporte_Multa_1: '',
          Numero_Suporte_Multa_2: '',
          pp_phone_sms_modal: false,
          pp_phone_sms_modal_trigger: 'consult',
        });

        await config.setConfigSettings({
          minimumFetchIntervalMillis: 0,
        });

        await config.fetchAndActivate();

        if (!isMounted) {
          return;
        }

        const modalEnabled = config.getValue('pp_phone_sms_modal').asBoolean();

        const modalTrigger = config
          .getValue('pp_phone_sms_modal_trigger')
          .asString();

        setNumeroSuporte1(config.getValue('Numero_Suporte_Multa_1').asString());

        setNumeroSuporte2(config.getValue('Numero_Suporte_Multa_2').asString());

        setPhoneSmsModalEnabled(modalEnabled);

        setPhoneSmsModalTrigger(
          modalTrigger === 'screen' ? 'screen' : 'consult',
        );
      } catch (error) {
        // Em caso de erro, não bloqueia a tela
        setPhoneSmsModalEnabled(false);
      } finally {
        if (isMounted) {
          setRemoteConfigLoaded(true);
        }
      }
    }

    fetchRemoteConfig();

    return () => {
      isMounted = false;
    };
  }, [isFocused]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (!isAuthReady || !isLogged) {
      setShowPhoneVerificationModal(false);
      return;
    }

    if (!remoteConfigLoaded) {
      return;
    }

    if (!phoneSmsModalEnabled) {
      return;
    }

    if (phoneSmsModalTrigger !== 'screen') {
      return;
    }

    if (user?.phone_verified) {
      return;
    }

    setShowPhoneVerificationModal(true);
  }, [
    isFocused,
    isAuthReady,
    isLogged,
    remoteConfigLoaded,
    phoneSmsModalEnabled,
    phoneSmsModalTrigger,
    user?.phone_verified,
  ]);

  useEffect(() => {
    const wasVisible = wasOTPModalVisibleRef.current;
    wasOTPModalVisibleRef.current = shouldShowOTPModal;

    if (!wasVisible || shouldShowOTPModal) {
      return;
    }

    if (!otpModalOpenedForScreenTriggerRef.current) {
      return;
    }

    otpModalOpenedForScreenTriggerRef.current = false;

    if (!user?.phone_verified) {
      navigation.navigate('Initial');
    }
  }, [shouldShowOTPModal, user?.phone_verified, navigation]);

  // Animations
  const cardAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const pulseLoop = useRef<Animated.CompositeAnimation | null>(null);

  const ufItems = useMemo(() => {
    return detrans
      .filter(d => d.convenio_uf)
      .map(d => ({
        label: d.nome,
        value: d.convenio_uf,
      }));
  }, [detrans]);

  const docValido =
    docProprietario.length === VALID_CPF_LENGTH ||
    docProprietario.length === VALID_CNPJ_LENGTH;

  const docFormatado = useMemo(() => {
    const d = docProprietario;
    if (d.length <= VALID_CPF_LENGTH) {
      return d
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return d
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }, [docProprietario]);

  const bothInputsFilled = placa.length === VALID_PLATE_LENGTH && docValido;

  const validationFluxoNovo = useMemo(() => {
    if (!uf) {
      return false;
    }
    if (placa.length !== VALID_PLATE_LENGTH) {
      return false;
    }

    if (renavam.length < VALID_RENAVAM_MIN_LENGTH) {
      return false;
    }

    if (!docValido) {
      return false;
    }

    return true;
  }, [placa, renavam, docValido, uf]);

  const validationFluxoAntigo = useMemo(() => {
    if (!bothInputsFilled) {
      return false;
    }
    if (veiculoNaoEncontrado) {
      return uf !== '' && renavam.length >= VALID_RENAVAM_MIN_LENGTH;
    }
    if (!veiculo || veiculoLoading || veiculoError) {
      return false;
    }

    return true;
  }, [
    bothInputsFilled,
    veiculo,
    veiculoLoading,
    veiculoError,

    veiculoNaoEncontrado,
    uf,
    renavam,
  ]);

  const isFormValid = novoFluxoDebitoVeicular
    ? validationFluxoNovo
    : validationFluxoAntigo;

  const handleConsultar = useCallback(async () => {
    setIsLoading(true);
    try {
      const {data} = await api.post<PPConsultaDTO>(
        `/payments/inicia_consulta_debitos?uf=${uf}&placa=${placa}&renavam=${renavam}&doc_proprietario=${docProprietario}`,
      );

      analytics().logEvent('pp_consultou_debitos', {
        user_id: user?.user_id,
        consulta_id: data.consulta_id.$oid,
        uf,
        placa,
        possui_debitos: data.resultado.possui_debitos,
        app_version: appVersion,
        device_model: DeviceInfo.getModel(),
        os: Platform.OS,
        os_version: DeviceInfo.getSystemVersion(),
      });

      if (!data.resultado.possui_debitos) {
        navigation.navigate('ConfirmationScreen', {
          type: 'success',
          buttonAction: () => navigation.navigate('VehicleDebits'),
          buttonText: 'Tentar novamente',
          closeIconAction: () => navigation.navigate('Initial'),
          text: 'Não encontramos débitos para o veículo informado.',
          title: 'Nenhum débito encontrado',
        });
        return;
      }

      setVeiculo(null);
      setUf('');
      setRenavam('');
      setDocProprietario('');
      setPlaca('');

      navigation.navigate('PP_Debits', {
        consulta: data,
        placa,
        uf,
      });
    } catch (error: any) {
      let errorText =
        'Tentamos, mas não conseguimos consultar seus débitos agora. Aguarde alguns minutos e tente novamente. Se o problema persistir, fale com o nosso suporte.';
      if (error.response.data.message) {
        errorText = error?.response?.data?.message;
      }

      if (error.response.data.msg) {
        errorText = error?.response?.data?.msg;
      }

      navigation.navigate('ConfirmationScreen', {
        type: 'error',
        buttonAction: () => navigation.navigate('VehicleDebits'),
        buttonText: 'Tentar novamente',
        closeIconAction: () => navigation.navigate('Initial'),
        showSupport: true,
        text: errorText,
        title: 'Não foi possível consultar os débitos',
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigation, uf, placa, renavam, docProprietario]);

  const [fetchingFluxo, setFetchingFluxo] = useState(true);


  useEffect(() => {
    if (!isFocused || !isAuthReady || !isLogged) {
      return;
    }

    let isMounted = true;

    setIsLoading(true);

    async function fetchInitialData() {
      try {
        const [fluxoResponse, detransResponse] = await Promise.allSettled([
          api.get('/payments/fluxo_debito_veicular'),
          api.get('/payments/lista_detrans_pronto_paguei'),
        ]);

        if (!isMounted) {
          return;
        }

        if (
          fluxoResponse.status === 'fulfilled' &&
          fluxoResponse.value.data.fluxo_novo_debito_veicular !== undefined
        ) {
          setNovoFluxoDebitoVeicular(
            fluxoResponse.value.data.fluxo_novo_debito_veicular,
          );
        }

        if (detransResponse.status === 'fulfilled') {
          setDetrans(detransResponse.value.data.convenios);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setFetchingFluxo(false);
        }
      }
    }

    fetchInitialData();
    fetchTextoIndParcDebCombustivel();
    rastrear({status_tela: 'multas'});

    return () => {
      isMounted = false;
    };
  }, [isFocused, isAuthReady, isLogged]);

  const handleConsultarPress = useCallback(() => {
    if (
      isLogged &&
      phoneSmsModalEnabled &&
      phoneSmsModalTrigger === 'consult' &&
      !user?.phone_verified
    ) {
      setShowPhoneVerificationModal(true);
      return;
    }

    handleConsultar();
  }, [
    isLogged,
    phoneSmsModalEnabled,
    phoneSmsModalTrigger,
    user?.phone_verified,
    handleConsultar,
  ]);
  useEffect(() => {
    if (veiculoLoading) {
      pulseLoop.current = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 0.45,
            duration: 550,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 550,
            useNativeDriver: true,
          }),
        ]),
      );
      pulseLoop.current.start();
    } else {
      pulseLoop.current?.stop();
      pulseAnim.setValue(1);
    }
  }, [veiculoLoading, pulseAnim]);

  useEffect(() => {
    Animated.timing(cardAnim, {
      toValue: bothInputsFilled ? 1 : 0,
      duration: 220,
      useNativeDriver: true,
    }).start();
  }, [bothInputsFilled, cardAnim]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);

  const clearPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
    setVeiculoPolling(false);
    setVeiculoLoading(false);
  }, []);

  const startPolling = useCallback(
    (
      consultaId: string,
      setUf: (v: string) => void,
      setRenavam: (v: string) => void,
      setVeiculoNaoEncontrado: (v: boolean) => void,
      isMounted: boolean,
    ) => {
      if (isPollingRef.current) {
        return;
      }
      isPollingRef.current = true;
      setVeiculoPolling(true);

      const poll = async () => {
        try {
          const {data} = await api.get(
            `/status_consulta_veiculo?consulta_id=${consultaId}`,
          );

          if (!isMounted) {
            return;
          }

          if (data.status === 'processando') {
            return;
          }

          clearPolling();

          if (data.status === 'concluido') {
            const veiculoData = data['dados-veiculo'];
            setVeiculo(veiculoData);
            setUf(veiculoData.uf);
            setRenavam(veiculoData.renavam);
          } else if (data.status === 'nao_encontrado') {
            setVeiculoNaoEncontrado(true);
            setVeiculoError(
              'Não encontramos seu veículo automaticamente. Informe o RENAVAM e UF acima para continuar.',
            );
          } else {
            setVeiculoError(
              'Veículo não encontrado. Verifique a placa e o CPF.',
            );
          }
        } catch (err: any) {
          if (!isMounted) {
            return;
          }
          if (err?.response?.status === 404) {
            clearPolling();
            setVeiculoError(
              'Veículo não encontrado. Verifique a placa e o CPF.',
            );
          }
          // Erros de rede → ignorar, tentar no próximo intervalo
        }
      };

      intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    },
    [clearPolling],
  );

  async function fetchVehicle(
    placa: string,
    docProprietario: string,
    setUf: (v: string) => void,
    setRenavam: (v: string) => void,
    setVeiculoNaoEncontrado: (v: boolean) => void,
    isMounted: boolean,
  ) {
    clearPolling();
    setVeiculo(null);
    setVeiculoError(null);

    try {
      setVeiculoLoading(true);

      const response = await api.post(
        `/payments/consulta_dados_veiculo?placa=${placa}&doc_proprietario=${docProprietario}`,
      );

      if (!isMounted) {
        return;
      }

      if (response.data?.codigo === 'CONSULTA_EM_PROCESSAMENTO') {
        startPolling(
          response.data.consulta_id,
          setUf,
          setRenavam,
          setVeiculoNaoEncontrado,
          isMounted,
        );
        return; // veiculoLoading permanece true → shimmer continua visível
      }

      const veiculoData = response.data['dados-veiculo'];

      setVeiculo(veiculoData);
      setUf(veiculoData.uf);
      setRenavam(veiculoData.renavam);
    } catch ({response}: any) {
      if (!isMounted) {
        return;
      }
      if (response?.data) {
        if (response.data.veiculo_nao_encontrado) {
          setVeiculoNaoEncontrado(true);
          setVeiculoError(
            'Não encontramos seu veículo automaticamente. Informe o RENAVAM e UF acima para continuar.',
          );
        } else {
          setVeiculoError('Veículo não encontrado. Verifique a placa e o CPF.');
        }
      } else {
        setVeiculoError('Veículo não encontrado. Verifique a placa e o CPF.');
      }
    } finally {
      if (isMounted) {
        setVeiculoLoading(false);
        isMounted = false;
      }
    }
  }

  useEffect(() => {
    let isMounted = true;
    if (!novoFluxoDebitoVeicular && !fetchingFluxo) {
      if (isMounted) {
        setVeiculo(null);
        setVeiculoError(null);
        setVeiculoLoading(false);
        setVeiculoNaoEncontrado(false);
        setUf('');
        setRenavam('');
      }

      if (!bothInputsFilled) {
        return;
      }

      if (isMounted) {
        setVeiculoLoading(true);
      }

      const timer = setTimeout(
        () =>
          fetchVehicle(
            placa,
            docProprietario,
            setUf,
            setRenavam,
            setVeiculoNaoEncontrado,
            isMounted,
          ),
        VEHICLE_FETCH_DEBOUNCE_MS,
      );

      return () => clearTimeout(timer);
    }

    return () => {
      isMounted = false;
    };
  }, [placa, docProprietario, bothInputsFilled, novoFluxoDebitoVeicular]);

  if (isDisabled('multas')) {
    return <Maintenance showButton={false} />;
  }

  if (!remoteConfigLoaded || !isAuthReady || isLoading) {
    return <Loading />;
  }

  function handleClosePhoneVerificationModal() {
    if(phoneSmsModalTrigger === 'screen'){
      setShowPhoneVerificationModal(false);
      navigation.navigate('Initial');
    }else{
      setShowPhoneVerificationModal(false);
    }
  }

  // No app nativo, fechar o fluxo "screen" redireciona pra home normalmente,
  // então "Agora não"/"X" podem aparecer. No web essas rotas não existem
  // (navigate('Initial') não faz nada), então o fluxo "screen" precisa ficar
  // não-dispensável ali.
  const isPhoneVerificationDismissable =
    phoneSmsModalTrigger !== 'screen' || Platform.OS !== 'web';

  return (
    <>
      <CheckoutGetnetLoading
        visible={veiculoPolling}
        title="Consultando seu veículo"
        description="Aguarde enquanto buscamos os dados do veículo. Isso pode levar alguns instantes — não feche o aplicativo."
      />

      <PhoneVerificationModal
        visible={showPhoneVerificationModal}
        dismissable={isPhoneVerificationDismissable}
        onClose={handleClosePhoneVerificationModal}
        onValidate={() => {
          setShowPhoneVerificationModal(false);
          if (phoneSmsModalTrigger === 'screen') {
            otpModalOpenedForScreenTriggerRef.current = true;
          }
          setShouldShowOTPModal(true, {
            dismissable: isPhoneVerificationDismissable,
          });
        }}
      />

      <ProfileHeader />

      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
        <TouchableWithoutFeedback
          onPress={() => Platform.OS !== 'web' && Keyboard.dismiss()}>
          <Container
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{paddingBottom: RFPercentage(5)}}>
            {!isLoading && (
              <>
                <ScreenTitle title="Parcelar Multas e IPVA" />

                <Content>
                  <Form>
                    {novoFluxoDebitoVeicular && (
                      <InputGroup>
                        <Picker
                          label="Estado (UF)"
                          placeholder={{label: 'Selecione o estado', value: ''}}
                          items={ufItems}
                          value={uf}
                          onChangeValue={setUf}
                        />
                      </InputGroup>
                    )}

                    <InputGroup>
                      <Input
                        labelText="Placa do veículo"
                        keyboardType="default"
                        placeholder="Ex: ABC1D23"
                        mask={'[AAA]-[0-99]'}
                        onChangeText={(_formatted, extracted) =>
                          setPlaca(extracted?.toUpperCase() as string)
                        }
                        value={placa}
                        autoCapitalize="characters"
                      />
                    </InputGroup>

                    {novoFluxoDebitoVeicular && (
                      <InputGroup>
                        <Input
                          labelText="RENAVAM"
                          keyboardType="number-pad"
                          placeholder="Ex: 00000000000"
                          onChangeText={text =>
                            setRenavam(text.replace(/\D/g, ''))
                          }
                          value={renavam}
                          maxLength={11}
                        />
                      </InputGroup>
                    )}

                    <InputGroup>
                      <Input
                        labelText="CPF / CNPJ do proprietário"
                        keyboardType="number-pad"
                        placeholder="Digite o CPF ou CNPJ"
                        onChangeText={text => {
                          const digits = text
                            .replace(/\D/g, '')
                            .slice(0, VALID_CNPJ_LENGTH);
                          setDocProprietario(digits);
                        }}
                        value={docFormatado}
                        maxLength={18}
                      />
                    </InputGroup>

                    {!novoFluxoDebitoVeicular && veiculoNaoEncontrado && (
                      <>
                        <InputGroup>
                          <Picker
                            label="Estado (UF)"
                            placeholder={{
                              label: 'Selecione o estado',
                              value: '',
                            }}
                            items={ufItems}
                            value={uf}
                            onChangeValue={setUf}
                          />
                        </InputGroup>
                        <InputGroup>
                          <Input
                            labelText="RENAVAM"
                            keyboardType="number-pad"
                            placeholder="Ex: 00000000000"
                            onChangeText={text =>
                              setRenavam(text.replace(/\D/g, ''))
                            }
                            value={renavam}
                            maxLength={11}
                          />
                        </InputGroup>
                      </>
                    )}

                    {!novoFluxoDebitoVeicular && (
                      <VehicleCard
                        veiculo={veiculo}
                        veiculoLoading={veiculoLoading}
                        veiculoError={veiculoError}
                        bothInputsFilled={bothInputsFilled}
                        cardAnim={cardAnim}
                        pulseAnim={pulseAnim}
                      />
                    )}

                    <Button
                      title="Consultar débitos"
                      enabled={isFormValid}
                      onPress={handleConsultarPress}
                    />
                  </Form>

                  <View
                    style={{
                      flexDirection: 'row',
                      marginTop: RFValue(12),
                      justifyContent: 'center',
                    }}>
                    <LinkButton
                      action={() => navigation.navigate('PP_TrackRequests')}
                      enabled
                      text="Acompanhar minhas solicitações"
                    />
                  </View>

                  <TrustBadgesRow>
                    <TrustBadgeItem>
                      <SenatranBadge>
                        <SenatranSelo source={seloImage} resizeMode="contain" />
                        <SenatranTextWrapper>
                          <SenatranLabel>Credenciados pelo</SenatranLabel>
                          <SenatranTitle>SENATRAN</SenatranTitle>
                        </SenatranTextWrapper>
                      </SenatranBadge>
                    </TrustBadgeItem>

                    <TrustBadgeDivider />

                    <TrustBadgeItem>
                      <SenatranBadge>
                        <SenatranSelo
                          source={seloDescontoImage}
                          resizeMode="contain"
                        />
                        <TrustBadgeText>
                          MENOR taxa{'\n'}do Brasil{' '}
                        </TrustBadgeText>
                      </SenatranBadge>
                    </TrustBadgeItem>
                  </TrustBadgesRow>

                  {(numeroSuporte1 || numeroSuporte2) && (
                    <SupportContainer>
                      <SupportText>
                        Em alguns casos, entraremos em contato pelo numero{' '}
                        {numeroSuporte1 && (
                          <SupportPhone>{numeroSuporte1}</SupportPhone>
                        )}
                        {numeroSuporte1 && numeroSuporte2 && ' ou '}
                        {numeroSuporte2 && (
                          <SupportPhone>{numeroSuporte2}</SupportPhone>
                        )}{' '}
                        para confirmar seu pagamento.
                      </SupportText>
                    </SupportContainer>
                  )}
                </Content>
              </>
            )}
          </Container>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </>
  );
}
